/**
 * Authentication Service
 * 
 * Handles all authentication-related API calls.
 * Uses HttpOnly cookies for secure token storage.
 * 
 * @module services/auth
 * 
 * @example
 * // Login with Google
 * const { user } = await authService.googleLogin(credential);
 * 
 * @example
 * // Get current user
 * const user = await authService.getCurrentUser();
 * 
 * @example
 * // Logout
 * await authService.logout();
 */

import { apiClient, ApiResponse } from './api';
import { AuthUser, AuthResponse, AuthCheckResponse, TokenVerifyResponse } from '../types';
import { API_BASE_URL } from './api';

/**
 * OTP delivery method
 */
export type OtpMethod = 'email' | 'sms';

/**
 * Login attempt status from the backend
 */
export interface LoginAttemptStatus {
  isLocked: boolean;
  attemptCount: number;
  maxAttempts: number;
  remainingAttempts: number;
  lockRemainingSeconds: number;
  lockRemainingMinutes: number;
  lockedUntil: string | null;
}

/**
 * Courier login Step 1 response (credentials validated, choose OTP method)
 */
export interface CourierLoginStep1Response {
  credentialsValid: true;
  availableOtpMethods: OtpMethod[];
  maskedEmail: string;
  maskedPhone: string;
  loginStatus: LoginAttemptStatus;
  authenticated?: boolean;
  otpBypassed?: boolean;
  user?: AuthUser;
}

/**
 * Courier OTP sent response (after method selection)
 */
export interface SendOtpResponse {
  success: true;
  requiresOtp: true;
  otpSentTo: string;
  otpExpiresIn: number;
  deliveryMethod: OtpMethod;
}

/**
 * OTP verification status
 */
export interface OtpVerificationStatus {
  remainingAttempts?: number;
  isLocked?: boolean;
  lockRemainingSeconds?: number;
}

/**
 * Courier login response with attempt status (for backward compatibility)
 * @deprecated Use CourierLoginStep1Response instead
 */
export interface CourierLoginResponse extends AuthResponse {
  loginStatus: LoginAttemptStatus;
}

// =============================================================================
// AUTH SERVICE
// =============================================================================

/**
 * Authentication service object
 * 
 * Contains all authentication-related methods.
 * All methods use HttpOnly cookies for authentication.
 */
export const authService = {
  // ===========================================================================
  // LOGIN METHODS
  // ===========================================================================

  /**
   * Login with Google OAuth credential
   * 
   * Sends the Google OAuth credential token to the backend for verification.
   * On success, the backend sets an HttpOnly cookie with the access token.
   * 
   * @param credential - Google OAuth credential token from Google Sign-In
   * @returns Promise resolving to authenticated user data
   * 
   * @example
   * const { user } = await authService.googleLogin(googleCredential);
   * console.log('Logged in as:', user.email);
   */
  googleLogin: async (credential: string): Promise<AuthResponse> => {
    const response = await apiClient.post<ApiResponse<AuthResponse>>(
      '/auth/google',
      { credential }
    );
    return response.data.data;
  },

  /**
   * Start SSO login (SAML redirect)
   *
   * This flow requires a full-page redirect to the backend, which then redirects
   * to the IdP (Google Workspace). After successful auth, the backend sets the
   * HttpOnly cookie and redirects back to the portal.
   */
  startSsoLogin: (): void => {
    const samlLoginUrl = `${API_BASE_URL.replace(/\/$/, '')}/api/v1/auth/sso/login`;
    window.location.href = samlLoginUrl;
  },

  /**
   * Log an SSO domain rejection detected by frontend precheck.
   */
  logSsoDomainRejected: async (email: string): Promise<void> => {
    await apiClient.post('/auth/sso/domain-rejected', { email });
  },

  /**
   * Login with phone number and ID number (for couriers) - Step 1
   * 
   * Validates credentials (phone + ID number).
   * Does NOT send OTP - returns available methods for user to choose.
   * 
   * @param username - Courier's phone number
   * @param idNumber - Courier's Israeli ID number (תעודת זהות)
   * @returns Promise resolving to credentials validation response with available OTP methods
   * 
   * @example
   * const result = await authService.courierLogin('0549211188', '123456789');
   * if (result.credentialsValid) {
   *   console.log('Available OTP methods:', result.availableOtpMethods);
   * }
   */
  courierLogin: async (username: string, idNumber: string): Promise<CourierLoginStep1Response> => {
    const response = await apiClient.post<ApiResponse<CourierLoginStep1Response>>(
      '/auth/courier/login',
      { username, idNumber }
    );
    return response.data.data;
  },

  /**
   * Send OTP via selected method (Step 2 of courier login)
   * 
   * Sends OTP code to user via their chosen method (email or SMS).
   * 
   * @param username - Courier's phone number
   * @param method - OTP delivery method ('email' or 'sms')
   * @returns Promise resolving to OTP sent confirmation
   * 
   * @example
   * const result = await authService.sendCourierOtp('0549211188', 'sms');
   * if (result.success) {
   *   console.log('OTP sent to:', result.otpSentTo);
   * }
   */
  sendCourierOtp: async (username: string, idNumber: string, method: OtpMethod): Promise<SendOtpResponse> => {
    const response = await apiClient.post<ApiResponse<SendOtpResponse>>(
      '/auth/courier/send-otp',
      { username, idNumber, method }
    );
    return response.data.data;
  },

  /**
   * Verify OTP code (Step 3 of courier login)
   * 
   * Verifies the OTP code sent to user's email.
   * On success, completes login and sets HttpOnly cookie.
   * 
   * @param username - Courier's username
   * @param code - 6-digit OTP code
   * @returns Promise resolving to authenticated user data
   * 
   * @example
   * const { user } = await authService.verifyCourierOtp('0549211188', '123456');
   */
  verifyCourierOtp: async (username: string, idNumber: string, code: string): Promise<AuthResponse> => {
    const response = await apiClient.post<ApiResponse<AuthResponse>>(
      '/auth/courier/verify-otp',
      { username, idNumber, code }
    );
    return response.data.data;
  },

  /**
   * Resend OTP code via selected method
   * 
   * Sends a new OTP code via the selected method. Invalidates previous code.
   * 
   * @param username - Courier's phone number
   * @param method - OTP delivery method ('email' or 'sms')
   * @returns Promise resolving to OTP sent confirmation
   * 
   * @example
   * const result = await authService.resendCourierOtp('0549211188', 'sms');
   * if (result.success) {
   *   console.log('OTP sent to:', result.otpSentTo);
   * }
   */
  resendCourierOtp: async (username: string, idNumber: string, method: OtpMethod): Promise<SendOtpResponse> => {
    const response = await apiClient.post<ApiResponse<SendOtpResponse>>(
      '/auth/courier/resend-otp',
      { username, idNumber, method }
    );
    return response.data.data;
  },

  /**
   * Check login status for a username
   * 
   * Use this before attempting login to check if account is locked.
   * 
   * @param username - Username to check
   * @returns Promise resolving to login status
   * 
   * @example
   * const { loginStatus } = await authService.checkLoginStatus('0549211188');
   * if (loginStatus.isLocked) {
   *   console.log('Account locked for', loginStatus.lockRemainingMinutes, 'minutes');
   * }
   */
  checkLoginStatus: async (username: string): Promise<{ loginStatus: LoginAttemptStatus }> => {
    const response = await apiClient.get<ApiResponse<{ loginStatus: LoginAttemptStatus }>>(
      `/auth/courier/login-status/${encodeURIComponent(username)}`
    );
    return response.data.data;
  },

  // ===========================================================================
  // SESSION METHODS
  // ===========================================================================

  /**
   * Get current authenticated user
   * 
   * Fetches the currently logged-in user's data.
   * Uses the HttpOnly cookie for authentication.
   * Throws an error if not authenticated.
   * 
   * @returns Promise resolving to current user data
   * @throws Error if not authenticated (401)
   * 
   * @example
   * try {
   *   const user = await authService.getCurrentUser();
   *   console.log('Current user:', user.fullName);
   * } catch {
   *   console.log('Not authenticated');
   * }
   */
  getCurrentUser: async (): Promise<AuthUser> => {
    const response = await apiClient.get<ApiResponse<AuthUser>>('/auth/me');
    return response.data.data;
  },

  /**
   * Complete self-provisioned profile (phone + last 4 ID digits).
   */
  completeSelfProfile: async (data: {
    phone: string;
    idNumberLast4: string;
  }): Promise<AuthUser> => {
    const response = await apiClient.patch<ApiResponse<AuthUser>>(
      '/users/me/complete-profile',
      data,
    );
    return response.data.data;
  },

  /**
   * Check if user is authenticated
   * 
   * Safe method that doesn't throw errors.
   * Returns authentication status and user data if authenticated.
   * 
   * @returns Promise resolving to authentication status
   * 
   * @example
   * const { authenticated, user } = await authService.checkAuth();
   * if (authenticated) {
   *   console.log('Welcome back,', user.fullName);
   * }
   */
  checkAuth: async (): Promise<AuthCheckResponse> => {
    try {
      const user = await authService.getCurrentUser();
      return { authenticated: true, user };
    } catch {
      return { authenticated: false };
    }
  },

  /**
   * Verify if the current token is valid
   * 
   * Lightweight check that doesn't return user data.
   * Useful for periodic token validation.
   * 
   * @returns Promise resolving to true if token is valid
   * 
   * @example
   * const isValid = await authService.verifyToken();
   * if (!isValid) {
   *   redirectToLogin();
   * }
   */
  verifyToken: async (): Promise<boolean> => {
    try {
      const response = await apiClient.get<ApiResponse<TokenVerifyResponse>>(
        '/auth/verify'
      );
      return response.data.data.valid;
    } catch {
      return false;
    }
  },

  // ===========================================================================
  // LOGOUT
  // ===========================================================================

  /**
   * Logout current user
   * 
   * Clears the authentication cookie on the server.
   * Does not throw errors - always succeeds locally.
   * 
   * @returns Promise that resolves when logout is complete
   * 
   * @example
   * await authService.logout();
   * redirectToLogin();
   */
  logout: async (): Promise<void> => {
    try {
      await apiClient.post('/auth/logout');
    } catch {
      // Ignore logout errors - user is logged out locally regardless
    }
  },
};

// =============================================================================
// EXPORTS
// =============================================================================

// Re-export types for convenience
export type { AuthUser, AuthResponse, AuthCheckResponse } from '../types';

export default authService;
